import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, cn, EmptyState, PlusIcon, Toggle } from '@playerbase/ui';
import type { BuildMarker } from '@playerbase/types';

import { useAuth } from '../../auth/use-auth.js';
import {
  archiveBuildMarker,
  createBuildMarker,
  unarchiveBuildMarker,
  updateBuildMarker,
} from '../../games/build-markers-data.js';
import { STAGE_BADGE_CLASSES } from '../../games/stage-colour-classes.js';
import { fetchBuildMarkersForGame } from '../../shell/shell-data.js';
import { useShell } from '../../shell/use-shell.js';
import {
  BuildFormModal,
  type BuildFormValues,
} from './builds/build-form-modal.js';
import { ArchiveBuildDialog } from './builds/archive-build-dialog.js';
import { ReleaseNotesModal } from './builds/release-notes-modal.js';

function formatShipped(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function GameSettingsBuildsPage(): JSX.Element {
  const { activeGame, devStages, refreshBuilds, buildMarkersVersion } = useShell();
  const { user } = useAuth();

  // Local list separate from shell context: this page shows archived rows
  // behind a toggle, while the shell context only ever holds active builds
  // for the topbar pill.
  const [markers, setMarkers] = useState<BuildMarker[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BuildMarker | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<BuildMarker | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [notesTarget, setNotesTarget] = useState<BuildMarker | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (activeGame === null) return;
    setLoading(true);
    const next = await fetchBuildMarkersForGame(activeGame.id, {
      includeArchived: true,
    });
    setMarkers(next);
    setLoading(false);
  }, [activeGame]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime: shell context bumps buildMarkersVersion whenever a build_markers
  // row changes for the active game. We re-pull the full list (with archived
  // included) so changes from the topbar quick-create or another tab land
  // here without a page refresh.
  useEffect(() => {
    if (buildMarkersVersion === 0) return;
    void reload();
  }, [buildMarkersVersion, reload]);

  const stageById = useMemo(() => {
    const map = new Map<string, (typeof devStages)[number]>();
    for (const s of devStages) map.set(s.id, s);
    return map;
  }, [devStages]);

  const visibleMarkers = useMemo(
    () => (showArchived ? markers : markers.filter((m) => m.archivedAt === null)),
    [markers, showArchived],
  );

  const archivedCount = useMemo(
    () => markers.filter((m) => m.archivedAt !== null).length,
    [markers],
  );

  const handleCreate = useCallback(
    async (values: BuildFormValues): Promise<void> => {
      if (activeGame === null) return;
      await createBuildMarker({
        gameId: activeGame.id,
        label: values.label,
        stageId: values.stageId,
        notes: values.notes,
        releasedAt: values.releasedAt,
        createdByUserId: user?.id ?? null,
      });
      await reload();
      await refreshBuilds();
      setFormOpen(false);
    },
    [activeGame, user, reload, refreshBuilds],
  );

  const handleEdit = useCallback(
    async (values: BuildFormValues): Promise<void> => {
      if (editing === null) return;
      await updateBuildMarker(editing.id, {
        label: values.label,
        stageId: values.stageId,
        notes: values.notes,
        releasedAt: values.releasedAt,
      });
      await reload();
      await refreshBuilds();
      setEditing(null);
    },
    [editing, reload, refreshBuilds],
  );

  const handleArchive = useCallback(async (): Promise<void> => {
    if (archiveTarget === null) return;
    setArchiveBusy(true);
    try {
      await archiveBuildMarker(archiveTarget.id);
      await reload();
      await refreshBuilds();
    } finally {
      setArchiveBusy(false);
      setArchiveTarget(null);
    }
  }, [archiveTarget, reload, refreshBuilds]);

  const handleUnarchive = useCallback(
    async (marker: BuildMarker): Promise<void> => {
      await unarchiveBuildMarker(marker.id);
      await reload();
      await refreshBuilds();
    },
    [reload, refreshBuilds],
  );

  if (activeGame === null) {
    return (
      <div className="px-6 py-10 text-sm text-text-muted">
        Select a game to manage builds.
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold text-text">Builds</h1>
        <p className="text-sm text-text-muted">
          Every build marker for {activeGame.name}. Mark a build, change its stage, or archive
          mistakes — references from Issues and feedback stay intact.
        </p>
      </header>

      {markers.length === 0 && !loading && (
        <EmptyState
          title="No builds logged yet"
          description="Log your first build to start anchoring feedback in time."
          cta={
            <Button
              variant="primary"
              size="md"
              disabled={devStages.length === 0}
              onClick={() => setFormOpen(true)}
            >
              <PlusIcon />
              <span>New build</span>
            </Button>
          }
        />
      )}

      {markers.length > 0 && (
        <div className="flex flex-col gap-4">
          {/* Toolbar row above the list mirrors the pattern used by
              Playtest flows / Feedback forms / Watched channels: the
              add-thing button sits on the right; any list-level
              controls sit on the left. */}
          <div className="flex items-center justify-between gap-3">
            {archivedCount > 0 ? (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span id="show-archived-label">
                  Show archived ({archivedCount})
                </span>
                <Toggle
                  aria-label="Show archived builds"
                  aria-labelledby="show-archived-label"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
              </div>
            ) : (
              <span />
            )}
            <Button
              variant="primary"
              size="md"
              disabled={devStages.length === 0}
              onClick={() => setFormOpen(true)}
            >
              <PlusIcon />
              <span>New build</span>
            </Button>
          </div>

          <ul className="flex flex-col divide-y divide-border rounded-md border border-border bg-surface-2">
            {visibleMarkers.map((marker) => {
              const stage = stageById.get(marker.stageId);
              const archived = marker.archivedAt !== null;
              return (
                <li
                  key={marker.id}
                  className={cn(
                    'flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center',
                    archived && 'opacity-60',
                  )}
                >
                  <div className="flex flex-1 items-center gap-3">
                    <span className="font-mono text-sm text-text">{marker.label}</span>
                    {stage !== undefined && (
                      <span
                        className={cn(
                          'rounded-sm border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider',
                          STAGE_BADGE_CLASSES[stage.colour],
                        )}
                      >
                        {stage.label}
                      </span>
                    )}
                    {archived && <Badge>Archived</Badge>}
                    <Badge>{marker.origin}</Badge>
                  </div>
                  <div className="flex flex-col items-start gap-0.5 text-xs text-text-muted sm:items-end">
                    <span>{formatShipped(marker.releasedAt)}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 sm:ml-3">
                    {marker.notes !== null && marker.notes.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setNotesTarget(marker)}
                      >
                        Notes
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setEditing(marker)}>
                      Edit
                    </Button>
                    {archived ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleUnarchive(marker)}
                      >
                        Unarchive
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchiveTarget(marker)}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <BuildFormModal
        open={formOpen}
        mode="create"
        marker={null}
        devStages={devStages}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />

      <BuildFormModal
        open={editing !== null}
        mode="edit"
        marker={editing}
        devStages={devStages}
        onCancel={() => setEditing(null)}
        onSubmit={handleEdit}
      />

      <ArchiveBuildDialog
        open={archiveTarget !== null}
        buildLabel={archiveTarget?.label ?? ''}
        busy={archiveBusy}
        onConfirm={() => void handleArchive()}
        onCancel={() => setArchiveTarget(null)}
      />

      <ReleaseNotesModal
        open={notesTarget !== null}
        buildLabel={notesTarget?.label ?? ''}
        notes={notesTarget?.notes ?? ''}
        onClose={() => setNotesTarget(null)}
      />
      </div>
    </div>
  );
}
